/**
 * Maps app routes to the user-guide section a person on that page is
 * most likely looking for. The drawer searches the PDF text for the
 * `keyword` (case-insensitive substring match) and offers a "Jump to:
 * {label} →" banner that scrolls to the first matching page.
 *
 * Adding a new entry here is the only thing required to give a new app
 * surface contextual help. Keep `keyword` to a distinctive phrase from
 * the section's heading so it doesn't false-match earlier mentions.
 */

export interface HelpSection {
  /** Human-readable label shown in the suggestion banner. */
  label: string;
  /** Case-insensitive substring searched in the PDF's text. First page
   *  containing it becomes the jump target. */
  keyword: string;
}

interface HelpRouteEntry {
  /** Tested against `location.pathname`. First-match wins, so put more
   *  specific patterns earlier. */
  match: (path: string) => boolean;
  section: HelpSection;
}

const HELP_ROUTES: HelpRouteEntry[] = [
  // Sub-routes of /barrel/:id come first so they take precedence over
  // the bare /barrel/:id page.
  {
    match: (p) => /^\/barrel\/[^/]+\/tasting$/.test(p),
    section: { label: 'Logging a tasting', keyword: 'Logging a tasting' },
  },
  {
    match: (p) => /^\/barrel\/[^/]+\/analytics$/.test(p),
    section: { label: 'Per-barrel analytics', keyword: 'Per-barrel analytics' },
  },
  {
    match: (p) => /^\/liquid\/[^/]+\/tasting$/.test(p),
    section: { label: 'Logging a tasting', keyword: 'Logging a tasting' },
  },
  // Project sub-routes before /projects so /projects/new and
  // /projects/:id/edit don't accidentally fall through to the index.
  {
    match: (p) => /^\/projects\/[^/]+\/edit$/.test(p),
    section: { label: 'Projects', keyword: 'Projects — production workflow' },
  },
  {
    match: (p) => p === '/projects/new',
    section: { label: 'Projects', keyword: 'Projects — production workflow' },
  },
  {
    match: (p) => /^\/projects\/[^/]+$/.test(p),
    section: { label: 'Projects', keyword: 'Projects — production workflow' },
  },
  {
    match: (p) => /^\/barrel\/[^/]+$/.test(p),
    section: { label: 'Barrel detail page', keyword: 'barrel detail page' },
  },
  {
    match: (p) => /^\/liquid\/[^/]+$/.test(p),
    section: { label: 'The Liquid Detail page', keyword: 'Liquid Detail page' },
  },
  // The blend-into-stainless wizard is a sub-flow of §15's Liquid
  // actions; the narrowest match is its own subsection.
  {
    match: (p) => p === '/blends/new-vat',
    section: {
      label: 'Creating a blend in a stainless container',
      keyword: 'Creating a blend in a stainless container',
    },
  },

  { match: (p) => p === '/scan', section: { label: 'Scanning a barrel', keyword: 'Scanning a barrel' } },
  { match: (p) => p === '/create', section: { label: 'Creating a new barrel', keyword: 'Creating a new barrel' } },
  { match: (p) => p === '/catalog', section: { label: 'The barrel catalog', keyword: 'barrel catalog' } },
  { match: (p) => p === '/templates', section: { label: 'Saving + applying barrel templates', keyword: 'Saving + applying barrel templates' } },
  { match: (p) => p === '/blends', section: { label: 'Liquid actions + Liquid Detail + Vat-blends', keyword: 'Moving liquid around' } },
  { match: (p) => p === '/projects', section: { label: 'Projects', keyword: 'Projects — production workflow' } },
  { match: (p) => p === '/stats', section: { label: 'Stats', keyword: 'Stats — at-a-glance' } },
  { match: (p) => p === '/admin', section: { label: 'Admin dashboard', keyword: 'Admin dashboard' } },
  { match: (p) => p === '/profile', section: { label: 'Your profile', keyword: 'Your profile' } },
  // The standalone /kentucky route is retired (its UX moved into the
  // catalog's location tabs), so there's no entry to map. The guide's
  // "Kentucky warehouse view (retired)" section is still reachable from
  // the Sections sidebar.
  { match: (p) => p === '/', section: { label: 'Dashboard', keyword: 'Dashboard — your home' } },
];

export const findHelpSection = (pathname: string): HelpSection | null => {
  for (const entry of HELP_ROUTES) {
    if (entry.match(pathname)) return entry.section;
  }
  return null;
};
